/* RotaForm v2 — Central Operacional: dados + clientes inline + gerar rota + otimizar em 1 tela.

Fluxo:
1. Topo: dados (data, tipo, motorista, veículo)
2. Meio: autocomplete de clientes da base unificada → adiciona à fila local
3. Fila de paradas (ordem, cliente, cidade, tipo, prioridade) com editar/remover
4. Resumo operacional (qtd clientes, cidades distintas, prioridade alta)
5. Botão principal: "Salvar rota e Gerar Melhor Rota"
*/
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import {
  ArrowLeft, Save, Loader2, Route, Search, Plus, Trash2, Users, Building2, MapPin, Wand2,
  Truck, Calendar, Tag, Sparkles, AlertTriangle, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import {
  TIPOS_OPERACAO, TIPOS_ATENDIMENTO_PARADA, PRIORIDADES, badge,
  linkGoogleMaps, linkWaze, linkGoogleMapsRota,
} from "./_lib";

const PARADA_VAZIA = {
  cliente_admin_id: "",
  cliente_nome: "",
  cnpj_cpf: "",
  endereco: "",
  cidade: "",
  uf: "",
  telefone: "",
  tipo_atendimento: "entrega",
  prioridade: "media",
  observacoes: "",
};

// Heurística local para PREVIEW da otimização — mesma do backend
function otimizarLocal(paradas) {
  const PRIO = { alta: 0, media: 1, baixa: 2 };
  return [...paradas]
    .sort((a, b) => {
      const ca = (a.cidade || "").toUpperCase();
      const cb = (b.cidade || "").toUpperCase();
      if (ca !== cb) return ca < cb ? -1 : 1;
      const pa = PRIO[a.prioridade] ?? 1;
      const pb = PRIO[b.prioridade] ?? 1;
      if (pa !== pb) return pa - pb;
      const ea = (a.endereco || "").toUpperCase().split(" ").slice(0, 3).join(" ");
      const eb = (b.endereco || "").toUpperCase().split(" ").slice(0, 3).join(" ");
      return ea < eb ? -1 : ea > eb ? 1 : 0;
    })
    .map((p, i) => ({ ...p, ordem: i + 1 }));
}

function normalizeKey(s) {
  return (s || "").trim().toLowerCase().replace(/\s+/g, " ");
}

export default function RotaForm() {
  const navigate = useNavigate();
  const [motoristas, setMotoristas] = useState([]);
  const [veiculos, setVeiculos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    data: new Date().toISOString().slice(0, 10),
    tipo_operacao: "entrega",
    motorista_id: "",
    veiculo_id: "",
    observacoes: "",
  });

  // Fila local de paradas antes de salvar
  const [paradas, setParadas] = useState([]);
  // Editor inline de parada
  const [editor, setEditor] = useState(PARADA_VAZIA);
  const [editorIdx, setEditorIdx] = useState(-1);

  // Autocomplete cliente
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const carregar = async () => {
      try {
        const [m, v] = await Promise.all([
          api.get("/roteirizacao/motoristas", { params: { status: "ativo" } }),
          api.get("/roteirizacao/veiculos", { params: { status: "ativo" } }),
        ]);
        setMotoristas(m.data?.items || []);
        setVeiculos(v.data?.items || []);
      } catch { /* silencioso — usuário ainda pode digitar dados */ }
    };
    carregar();
  }, []);

  // Debounce autocomplete
  useEffect(() => {
    if (!q || q.length < 2) { setOpts([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/admin/clientes", { params: { q, limit: 8 } });
        setOpts(data?.items || []);
      } catch { setOpts([]); }
      setSearching(false);
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const enderecoInputRef = useRef(null);

  const selecionarCliente = (c) => {
    const endereco_parts = [c.endereco, c.bairro].filter(Boolean).join(", ");
    setEditor({
      ...PARADA_VAZIA,
      cliente_admin_id: c.id,
      cliente_nome: c.nome,
      cnpj_cpf: c.cnpj_cpf || "",
      endereco: endereco_parts,
      cidade: c.cidade || "",
      uf: c.uf || "",
      telefone: c.telefone || "",
      tipo_atendimento: form.tipo_operacao === "multipla" ? "entrega" : form.tipo_operacao,
    });
    setEditorIdx(-1);
    setQ("");
    setOpts([]);
    // Se cliente não tem endereço cadastrado, foca no campo + avisa
    if (!endereco_parts) {
      toast.info("Cliente sem endereço cadastrado — preencha manualmente abaixo.");
      setTimeout(() => enderecoInputRef.current?.focus(), 100);
    } else {
      inputRef.current?.focus();
    }
  };

  const adicionarParada = () => {
    if (!editor.cliente_nome?.trim()) { toast.error("Selecione um cliente"); return; }
    if (!editor.endereco?.trim()) { toast.error("Endereço obrigatório"); return; }

    // Detectar duplicidade local
    const k = normalizeKey(`${editor.endereco}|${editor.cidade}`);
    const dupIdx = paradas.findIndex((p) => normalizeKey(`${p.endereco}|${p.cidade}`) === k);
    if (dupIdx >= 0 && editorIdx !== dupIdx) {
      if (!window.confirm(
        `Este endereço já está em outra parada (${paradas[dupIdx].cliente_nome}). Adicionar mesmo assim? (Cancele para pular)`
      )) return;
    }

    if (editorIdx >= 0) {
      const novas = [...paradas];
      novas[editorIdx] = { ...editor, ordem: editorIdx + 1 };
      setParadas(novas);
      toast.success("Parada atualizada");
    } else {
      setParadas([...paradas, { ...editor, ordem: paradas.length + 1 }]);
      toast.success(`Cliente ${editor.cliente_nome.split(" ").slice(0, 3).join(" ")}… adicionado`);
    }
    setEditor(PARADA_VAZIA);
    setEditorIdx(-1);
  };

  const editarParada = (idx) => { setEditor({ ...paradas[idx] }); setEditorIdx(idx); };
  const removerParada = (idx) => {
    const novas = paradas.filter((_, i) => i !== idx).map((p, i) => ({ ...p, ordem: i + 1 }));
    setParadas(novas);
    if (editorIdx === idx) { setEditor(PARADA_VAZIA); setEditorIdx(-1); }
  };

  const otimizarPreview = () => {
    if (paradas.length < 2) { toast.info("Mínimo 2 paradas para otimizar"); return; }
    setParadas(otimizarLocal(paradas));
    toast.success(`Rota otimizada: ${paradas.length} paradas reorganizadas por cidade + prioridade`);
  };

  // Resumo operacional
  const resumo = useMemo(() => {
    const cidades = new Set(paradas.map((p) => (p.cidade || "").toUpperCase()).filter(Boolean));
    const ufs = new Set(paradas.map((p) => (p.uf || "").toUpperCase()).filter(Boolean));
    const altaPrio = paradas.filter((p) => p.prioridade === "alta").length;
    const porTipo = paradas.reduce((acc, p) => {
      acc[p.tipo_atendimento] = (acc[p.tipo_atendimento] || 0) + 1;
      return acc;
    }, {});
    return { qtd: paradas.length, cidades: [...cidades], ufs: [...ufs], altaPrio, porTipo };
  }, [paradas]);

  const salvar = async () => {
    if (!form.data) { toast.error("Data é obrigatória"); return; }
    if (!form.tipo_operacao) { toast.error("Tipo de operação obrigatório"); return; }
    setBusy(true);
    try {
      // 1) Criar rota
      const payload = {
        data: form.data,
        tipo_operacao: form.tipo_operacao,
        motorista_id: form.motorista_id || null,
        veiculo_id: form.veiculo_id || null,
        observacoes: form.observacoes || null,
      };
      const { data: rota } = await api.post("/roteirizacao/rotas", payload);

      // 2) Anexar paradas em sequência (mantém ordem)
      let agrupadas = 0;
      for (const p of paradas) {
        try {
          const resp = await api.post(`/roteirizacao/rotas/${rota.id}/paradas`, p);
          if (resp.data?.alerta === "endereco_duplicado") {
            // backend detectou duplicidade — agrupa automaticamente nesta criação inicial
            await api.post(`/roteirizacao/rotas/${rota.id}/paradas`, { ...p, agrupar_endereco_duplicado: true });
            agrupadas += 1;
          }
        } catch {
          // ignora individual, log se quiser
        }
      }

      // 3) Otimizar no backend (já organizada localmente, mas garante consistência)
      if (paradas.length >= 2) {
        try { await api.post(`/roteirizacao/rotas/${rota.id}/otimizar`); } catch { /* nada */ }
      }

      toast.success(
        `Rota ${rota.numero} criada com ${paradas.length} cliente(s)` +
        (agrupadas ? ` · ${agrupadas} agrupada(s)` : "")
      );
      navigate(`/app/roteirizacao/rotas/${rota.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao criar rota");
    } finally { setBusy(false); }
  };

  const linkRotaMaps = linkGoogleMapsRota(paradas);
  const linkPrimeiroWaze = paradas[0] ? linkWaze(paradas[0].endereco, paradas[0].cidade, paradas[0].uf) : null;

  return (
    <div className="space-y-4 max-w-7xl mx-auto pb-20" data-testid="rota-form-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Link to="/app/roteirizacao"><Button variant="ghost" size="sm"><ArrowLeft size={14} /></Button></Link>
          <Route size={26} style={{ color: "#1B3A6B" }} />
          <div>
            <h1 className="text-2xl font-bold" style={{ color: "#1B3A6B" }}>Nova Rota — Central Operacional</h1>
            <p className="text-xs text-muted-foreground">Monte a rota do dia: dados → clientes → otimização → despacho.</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* COLUNA ESQUERDA: dados + autocomplete + editor de parada */}
        <div className="lg:col-span-2 space-y-4">
          {/* 1) DADOS DA ROTA */}
          <Card className="p-4">
            <h2 className="font-bold text-sm flex items-center gap-2 mb-3" style={{ color: "#1B3A6B" }}>
              <Calendar size={16} /> 1. Dados da operação
            </h2>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Data *</Label>
                <Input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} data-testid="input-data" />
              </div>
              <div>
                <Label className="text-xs">Tipo de operação *</Label>
                <Select value={form.tipo_operacao} onValueChange={(v) => setForm({ ...form, tipo_operacao: v })}>
                  <SelectTrigger data-testid="sel-tipo-op"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPOS_OPERACAO.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Motorista</Label>
                <Select value={form.motorista_id || "_none"} onValueChange={(v) => setForm({ ...form, motorista_id: v === "_none" ? "" : v })}>
                  <SelectTrigger data-testid="sel-motorista"><SelectValue placeholder="Selecionar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">— Sem motorista ainda</SelectItem>
                    {motoristas.map((m) => <SelectItem key={m.id} value={m.id}>{m.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Veículo</Label>
                <Select value={form.veiculo_id || "_none"} onValueChange={(v) => setForm({ ...form, veiculo_id: v === "_none" ? "" : v })}>
                  <SelectTrigger data-testid="sel-veiculo"><SelectValue placeholder="Selecionar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">— Sem veículo ainda</SelectItem>
                    {veiculos.map((v) => <SelectItem key={v.id} value={v.id}>{v.placa} · {v.modelo}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Observações</Label>
                <Textarea rows={2} value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
              </div>
            </div>
          </Card>

          {/* 2) CLIENTES DA ROTA */}
          <Card className="p-4">
            <h2 className="font-bold text-sm flex items-center gap-2 mb-3" style={{ color: "#1B3A6B" }}>
              <Users size={16} /> 2. Clientes da rota
              <span className="text-xs font-normal text-muted-foreground ml-2">
                Digite parte do nome, CNPJ ou cidade — a base unificada (4.137+ clientes) responde em tempo real.
              </span>
            </h2>

            {/* Autocomplete */}
            <div className="relative">
              <Search size={16} className="absolute left-3 top-3 text-muted-foreground" />
              <Input
                ref={inputRef}
                className="pl-10 h-11"
                placeholder="Ex: WMS · 93.209.765/0333-92 · cabo · sao paulo..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="rota-busca-cliente"
                autoFocus
              />
              {searching && (
                <Loader2 size={14} className="absolute right-3 top-3.5 animate-spin text-muted-foreground" />
              )}
            </div>

            {opts.length > 0 && (
              <div className="mt-2 border rounded-lg max-h-64 overflow-y-auto divide-y" data-testid="rota-opts">
                {opts.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => selecionarCliente(c)}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 transition flex items-start justify-between gap-2"
                    data-testid={`rota-opt-${c.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{c.nome}</div>
                      <div className="text-[10px] text-muted-foreground font-mono">
                        {c.cnpj_cpf || ""} {c.cidade ? `· ${c.cidade}${c.uf ? `/${c.uf}` : ""}` : ""}
                      </div>
                    </div>
                    <Badge variant="outline" className="text-[10px] shrink-0">
                      <Building2 size={9} className="mr-1" />
                      {c.origem === "locativo" ? "Base" : "CRM"}
                    </Badge>
                  </button>
                ))}
              </div>
            )}

            {/* Editor inline */}
            {(editor.cliente_nome || editorIdx >= 0) && (
              <div className="mt-4 p-3 bg-blue-50/40 border border-blue-200 rounded-lg space-y-2" data-testid="editor-parada">
                <div className="flex items-center justify-between">
                  <div className="font-bold text-sm" style={{ color: "#1B3A6B" }}>
                    {editorIdx >= 0 ? `Editando parada #${editorIdx + 1}` : "Nova parada"}: {editor.cliente_nome}
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => { setEditor(PARADA_VAZIA); setEditorIdx(-1); }}>
                    Cancelar
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="col-span-2">
                    <Label className="text-[10px]">Endereço *</Label>
                    <Input
                      ref={enderecoInputRef}
                      value={editor.endereco}
                      onChange={(e) => setEditor({ ...editor, endereco: e.target.value })}
                      className={`h-8 text-sm ${!editor.endereco ? "border-amber-400 bg-amber-50" : ""}`}
                      placeholder={!editor.endereco ? "Cliente sem endereço cadastrado — digite aqui" : ""}
                      data-testid="input-endereco-editor"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px]">Cidade</Label>
                    <Input value={editor.cidade} onChange={(e) => setEditor({ ...editor, cidade: e.target.value })} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-[10px]">UF</Label>
                    <Input value={editor.uf} onChange={(e) => setEditor({ ...editor, uf: e.target.value.toUpperCase().slice(0, 2) })} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-[10px]">Telefone</Label>
                    <Input value={editor.telefone} onChange={(e) => setEditor({ ...editor, telefone: e.target.value })} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-[10px]">Tipo</Label>
                    <Select value={editor.tipo_atendimento} onValueChange={(v) => setEditor({ ...editor, tipo_atendimento: v })}>
                      <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TIPOS_ATENDIMENTO_PARADA.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px]">Prioridade</Label>
                    <Select value={editor.prioridade} onValueChange={(v) => setEditor({ ...editor, prioridade: v })}>
                      <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PRIORIDADES.map((p) => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-[10px]">Observações</Label>
                    <Input value={editor.observacoes} onChange={(e) => setEditor({ ...editor, observacoes: e.target.value })} className="h-8 text-sm" />
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={adicionarParada}
                  className="text-white w-full mt-1"
                  style={{ background: "#F97316" }}
                  data-testid="btn-add-parada-fila"
                >
                  <Plus size={14} className="mr-1" /> {editorIdx >= 0 ? "Atualizar parada" : "Adicionar à rota"}
                </Button>
              </div>
            )}
          </Card>

          {/* 3) FILA DE PARADAS */}
          <Card>
            <div className="p-3 border-b flex items-center justify-between flex-wrap gap-2">
              <h2 className="font-bold text-sm flex items-center gap-2" style={{ color: "#1B3A6B" }}>
                <MapPin size={16} /> 3. Sequência da rota ({paradas.length})
              </h2>
              {paradas.length >= 2 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={otimizarPreview}
                  className="text-orange-700 border-orange-300 hover:bg-orange-50"
                  data-testid="btn-otimizar-preview"
                >
                  <Wand2 size={12} className="mr-1" /> Reordenar agora (cidade + prioridade)
                </Button>
              )}
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">#</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Cidade</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Prio</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paradas.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    Busque o primeiro cliente acima ☝️ e ele aparece aqui.
                  </TableCell></TableRow>
                )}
                {paradas.map((p, i) => {
                  const bpr = badge(p.prioridade, PRIORIDADES);
                  return (
                    <TableRow key={i} data-testid={`fila-${i}`}>
                      <TableCell className="font-mono font-bold" style={{ color: "#F97316" }}>{p.ordem}</TableCell>
                      <TableCell>
                        <div className="font-medium text-sm">{p.cliente_nome}</div>
                        <div className="text-[10px] text-muted-foreground truncate max-w-xs">{p.endereco}</div>
                      </TableCell>
                      <TableCell className="text-xs">{p.cidade}{p.uf ? `/${p.uf}` : ""}</TableCell>
                      <TableCell className="text-xs uppercase">{p.tipo_atendimento}</TableCell>
                      <TableCell><Badge className={bpr.cls + " border text-[10px]"}>{bpr.label}</Badge></TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => editarParada(i)} data-testid={`editar-${i}`}>✏️</Button>
                        <Button size="sm" variant="ghost" onClick={() => removerParada(i)} data-testid={`remover-${i}`}>
                          <Trash2 size={12} className="text-red-600" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </div>

        {/* COLUNA DIREITA: resumo operacional + ações */}
        <div className="space-y-4 lg:sticky lg:top-4 self-start">
          <Card className="p-4 border-l-4" style={{ borderLeftColor: "#F97316" }}>
            <h2 className="font-bold text-sm flex items-center gap-2 mb-3" style={{ color: "#F97316" }}>
              <Sparkles size={16} /> Resumo operacional
            </h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground"><Users size={12} className="inline mr-1" /> Clientes</span>
                <span className="font-bold" data-testid="resumo-clientes">{resumo.qtd}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground"><Building2 size={12} className="inline mr-1" /> Cidades</span>
                <span className="font-bold" data-testid="resumo-cidades">{resumo.cidades.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground"><MapPin size={12} className="inline mr-1" /> UFs</span>
                <span className="font-bold">{resumo.ufs.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground"><AlertTriangle size={12} className="inline mr-1 text-red-600" /> Alta prioridade</span>
                <span className="font-bold text-red-700">{resumo.altaPrio}</span>
              </div>
              {Object.keys(resumo.porTipo).length > 0 && (
                <div className="pt-2 border-t">
                  <span className="text-xs text-muted-foreground"><Tag size={11} className="inline mr-1" /> Por tipo:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {Object.entries(resumo.porTipo).map(([t, n]) => (
                      <Badge key={t} variant="outline" className="text-[10px]">
                        {TIPOS_ATENDIMENTO_PARADA.find((x) => x.v === t)?.label || t}: {n}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {resumo.cidades.length > 0 && (
                <div className="pt-2 border-t">
                  <span className="text-xs text-muted-foreground">Cidades:</span>
                  <div className="text-[10px] mt-1 leading-relaxed">
                    {resumo.cidades.join(" · ")}
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Pré-visualização Maps/Waze antes mesmo de salvar */}
          {paradas.length > 0 && (
            <Card className="p-3">
              <p className="text-xs font-bold mb-2 text-muted-foreground">Pré-visualização (antes de salvar)</p>
              <div className="grid grid-cols-2 gap-2">
                {linkRotaMaps && (
                  <a href={linkRotaMaps} target="_blank" rel="noopener noreferrer">
                    <Button size="sm" variant="outline" className="w-full" data-testid="preview-maps">
                      <ExternalLink size={12} className="mr-1" /> Maps
                    </Button>
                  </a>
                )}
                {linkPrimeiroWaze && (
                  <a href={linkPrimeiroWaze} target="_blank" rel="noopener noreferrer">
                    <Button size="sm" variant="outline" className="w-full bg-blue-50 text-blue-800" data-testid="preview-waze">
                      <ExternalLink size={12} className="mr-1" /> Waze 1ª
                    </Button>
                  </a>
                )}
              </div>
              <p className="text-[10px] text-muted-foreground mt-2">
                Maps abre rota completa · Waze abre na 1ª parada (Waze não suporta multi-stop)
              </p>
            </Card>
          )}

          {/* Botão principal — Gerar Melhor Rota */}
          <Card className="p-3">
            <Button
              onClick={salvar}
              disabled={busy || paradas.length === 0}
              className="w-full text-white h-12 text-base shadow-lg"
              style={{ background: "linear-gradient(135deg, #F97316, #ea580c)" }}
              data-testid="btn-gerar-melhor-rota"
            >
              {busy ? (
                <><Loader2 className="animate-spin mr-2" size={16} /> Despachando…</>
              ) : (
                <><Truck size={16} className="mr-2" /> Salvar e Gerar Melhor Rota</>
              )}
            </Button>
            <p className="text-[10px] text-center text-muted-foreground mt-2">
              Cria a rota, anexa as {paradas.length || "—"} parada(s) e otimiza no backend
            </p>
            <Link to="/app/roteirizacao">
              <Button variant="ghost" className="w-full mt-1 text-xs h-8">Cancelar</Button>
            </Link>
          </Card>
        </div>
      </div>
    </div>
  );
}
